if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.outOfStockButton = this.querySelector('#outofstock');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.handleCustomVariantSelect();
      }

      connectedCallback() {
        this.setupStickyButtonsOnScroll();
      }

      disconnectedCallback() {
        if (this._stickyObserver && this._buttonsEl) {
          this._stickyObserver.unobserve(this._buttonsEl);
        }
        window.removeEventListener('scroll', this._onScrollSticky, { passive: true });
      }

      setupStickyButtonsOnScroll() {
        const buttons = this.querySelector('.product-form__buttons');
        if (!buttons) return;
        this._buttonsEl = buttons;

        if (window.self !== window.top) {
          buttons.classList.remove('position-sticky');
          return;
        }

        if (this._stickyObserver) {
          this._stickyObserver.unobserve(buttons);
        }
        window.removeEventListener('scroll', this._onScrollSticky, { passive: true });

        // Wrap buttons to keep layout height fixed when sticky kicks in
        let wrapper = buttons.parentElement;
        if (wrapper && !wrapper.classList.contains('product-form__buttons-wrapper')) {
          wrapper = document.createElement('div');
          wrapper.className = 'product-form__buttons-wrapper';
          buttons.parentNode?.insertBefore(wrapper, buttons);
          wrapper.appendChild(buttons);
        }

        const ORIGINAL_PLACE_VISIBLE_TOP = 150;
        this._onScrollSticky = () => {
          if (!buttons.classList.contains('position-sticky')) return;
          if (this._buttonsNaturalTop == null) return;
          const wouldBeTop = this._buttonsNaturalTop - window.scrollY;
          if (wouldBeTop >= 0 && wouldBeTop < ORIGINAL_PLACE_VISIBLE_TOP) {
            buttons.classList.remove('position-sticky');
            if (wrapper) wrapper.style.height = ''; // Reset height
            this._buttonsNaturalTop = buttons.getBoundingClientRect().top + window.scrollY;
          }
        };
        window.addEventListener('scroll', this._onScrollSticky, { passive: true });

        this._stickyObserver = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            const top = entry.boundingClientRect.top;
            if (top < 0) {
              const rect = buttons.getBoundingClientRect();
              // Lock the wrapper height BEFORE applying fixed position
              if (wrapper) wrapper.style.height = `${rect.height}px`;

              buttons.classList.add('position-sticky');
              this._buttonsNaturalTop = window.scrollY + top;
            }
          },
          { threshold: 0 }
        );
        this._stickyObserver.observe(buttons);
        if (!buttons.classList.contains('position-sticky')) {
          this._buttonsNaturalTop = buttons.getBoundingClientRect().top + window.scrollY;
        }
      }

      handleCustomVariantSelect() {
        const ELM_selectOption = document.querySelectorAll('[name="select-variant"]');
        if (!ELM_selectOption) return;
        ELM_selectOption.forEach(select => {
          select.addEventListener('change', e => {
            const selectedOption = e.target?.options[e.target.selectedIndex];
            const variantId = e?.target?.value;
            const ELM_card = select.closest('.card-product-wrapper');
            const ELM_productForm = ELM_card.querySelector('product-form');
            const ELM_price = ELM_card.querySelector('.price-item');
            const price = selectedOption?.dataset?.price;
            if (variantId && ELM_productForm) {
              ELM_productForm.querySelector('[name=id]').value = variantId;
            }
            if (ELM_price && price) {
              ELM_price.innerHTML = price;
            }
          });
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, variant) {
        const ELM_similarProducts = document.querySelector('#pdp-sp');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.outOfStockButton?.classList?.remove('hidden');
          ELM_similarProducts?.classList?.remove('hidden');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.innerHTML = `${window.variantStrings.addToCart} <span class="price-item">$${(variant.price / 100.0).toFixed(2)}</span>`;
          this.outOfStockButton?.classList?.add('hidden');
          ELM_similarProducts?.classList?.add('hidden');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
